import React from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { ModeEditOutlineTwoTone } from "@mui/icons-material";
import { toast } from "react-toastify";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import {
  useLazyListCustomerNotesQuery,
  useUpdateCustomerNotesVMutation,
} from "store/services/customerApi";

import FormDrawer from "components/Shared/FormDrawer";
import CustomerNotesForm from "./CustomerNotesForm";
import AppTable from "components/Shared/AppTable";

import { useTablePagination } from "hooks";

const CustomerNoteList = () => {
  const { id } = useParams();

  const [showNoteForm, setShowNoteForm] = React.useState(false);

  const [selectedNote, setSelectedNote] = React.useState(null);

  const { currentPage, handlePageChange, handlePerRowsChange } =
    useTablePagination({
      perPage: 5,
    });
  const [perPage, setPerPage] = React.useState(5);

  const columns = [
    {
      name: "Content",
      selector: (row) => row?.content,
      cell: (row) => row?.content,
    },
    {
      name: "Type",
      selector: (row) => row?.type,
      cell: (row) => row?.type,
    },
    {
      name: "Created By",
      selector: (row) => row?.createdBy?.name,
      cell: (row) => row?.createdBy?.name || "N/A",
    },
    {
      name: "Updated By",
      selector: (row) => row?.updatedBy?.name,
      cell: (row) => row?.updatedBy?.name || "N/A",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <IconButton
            size="small"
            aria-label="Edit Note"
            onClick={() => {
              setSelectedNote(row);
              setShowNoteForm(true);
            }}
          >
            <ModeEditOutlineTwoTone />
          </IconButton>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [updateNote, { isLoading: updatingNote }] =
    useUpdateCustomerNotesVMutation();

  const [fetchNotes, { data: notesRes, isLoading, isFetching }] =
    useLazyListCustomerNotesQuery();

  const handleUpdateNote = async (data) => {
    try {
      await updateNote({
        customerId: id,
        noteId: data?.noteId,
        ...data,
      }).unwrap();
      toast.success("Note updated successfully!");
      handleCancelNote();
    } catch (err) {
      toast.error(err?.data?.message ?? "Failed to update note!");
    }
  };

  // methods
  const handleCancelNote = () => {
    setShowNoteForm(false);
    setSelectedNote(null);
  };

  const handleRowsPerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    handlePerRowsChange(newPerPage);
  };

  React.useEffect(() => {
    fetchNotes({
      customerId: id,
      page: currentPage,
      limit: perPage,
    });
  }, [currentPage, perPage, fetchNotes, id]);

  return (
    <>
      <BreadcrumbLayout.Paper>
        {notesRes ? (
          <AppTable
            columns={columns}
            data={notesRes?.notes ?? []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={notesRes?.totalItems ?? 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
          />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={2}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" marginLeft={1}>
              Loading Notes...
            </Typography>
          </Box>
        )}
      </BreadcrumbLayout.Paper>

      <FormDrawer
        title={selectedNote ? "Edit Note" : "Add Note"}
        open={showNoteForm}
        onClose={handleCancelNote}
      >
        <CustomerNotesForm
          isBusy={updatingNote}
          note={selectedNote}
          onSubmit={handleUpdateNote}
          onCancel={handleCancelNote}
        />
      </FormDrawer>
    </>
  );
};

export default CustomerNoteList;
